import { useMutation, useSuspenseQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { SettingsPageFrame } from '@eluve/blocks';
import { SimpleTooltip } from '@eluve/components';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Card,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  EluveCardContent,
  EluveCardHeader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Heading,
  Icon,
  Input,
  Separator,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { LocationBillingCodes } from '@eluve/frontend-feature-appointment';
import {
  LocationUsers,
  getLocationPageLayoutQuery,
  locationUsersFragment,
  tenantUsersFragment,
  updateLocationDetailsMutation,
  updateLocationDisplayNameMutation,
  updateLocationLogoMutation,
} from '@eluve/frontend-feature-location-hierarchy';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import {
  FeatureFlaggedComponent,
  useUploadGenericFile,
} from '@eluve/smart-blocks';

import { NewEhrForm } from './NewEhrForm';
import { SyncedLocationStats, SyncedSourceGrid } from './SyncedLocationCard';

const locationDetailsFormSchema = z.object({
  address: z.string().optional(),
  phoneNumber: z.string().optional(),
  externalEhrId: z.string().optional(),
  externalId: z.string().optional(),
  businessName: z.string().optional(),
  placeOfServiceCode: z.string().optional(),
  taxId: z.string().optional(),
  replyToEmail: z
    .string()
    .email()
    .optional()
    .or(z.literal('').transform(() => undefined)),
});

type LocationDetailsForm = z.infer<typeof locationDetailsFormSchema>;

/**
 * Inline-editable location title (pencil affordance), mirroring the
 * appointment title. Confirms with Enter/✓, cancels with Escape/✕.
 */
const EditableLocationName: React.FC<{
  name: string;
  isSaving: boolean;
  onSave: (next: string) => void;
}> = ({ name, isSaving, onSave }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(name);

  useEffect(() => setValue(name), [name]);

  const cancel = () => {
    setValue(name);
    setIsEditing(false);
  };

  const save = () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== name) {
      onSave(trimmed);
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Stack direction="horizontal" gap={2} align="center" fullWidth={false}>
        <Input
          autoFocus
          className="h-11 max-w-md text-2xl font-semibold"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              save();
            }
            if (e.key === 'Escape') {
              cancel();
            }
          }}
        />
        <Button size="sm" onClick={save} disabled={isSaving}>
          <Icon name="Check" size="auto" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={cancel}
          disabled={isSaving}
        >
          <Icon name="X" size="auto" />
        </Button>
      </Stack>
    );
  }

  return (
    <Stack direction="horizontal" gap={2} align="center" fullWidth={false}>
      <Heading>{name}</Heading>
      <SimpleTooltip label={t('tenantAdmin.location.editName')}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsEditing(true)}
          aria-label={t('tenantAdmin.location.editName')}
        >
          <Icon name="Pencil" size="auto" />
        </Button>
      </SimpleTooltip>
    </Stack>
  );
};

const locationPageQuery = graphql(
  `
    query GetLocationPage($tenantId: uuid!, $locationId: String!) {
      tenantsByPk(id: $tenantId) {
        __typename
        id
        ...TenantUsers
      }
      locationsByPk(tenantId: $tenantId, id: $locationId) {
        __typename
        id
        ...LocationUsers
      }
    }
  `,
  [locationUsersFragment, tenantUsersFragment],
);

const LocationContent: React.FC = () => {
  const { tenantId, locationId } = useParams() as {
    tenantId: string;
    locationId: string;
  };
  const toast = useToast();
  const { t } = useTranslation();

  // cache-and-network: render from cache immediately but refresh in the
  // background, so EMR-synced changes and users added elsewhere show up
  // without a hard refresh.
  const { data } = useSuspenseQuery(getLocationPageLayoutQuery, {
    variables: {
      locationId,
      tenantId,
    },
    fetchPolicy: 'cache-and-network',
  });

  useSuspenseQuery(locationPageQuery, {
    variables: {
      tenantId,
      locationId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { uploadGenericFileToGCS } = useUploadGenericFile(tenantId);

  const location = data.locationsByPk;
  const tenantExternalEhrs = data?.tenantsByPk?.externalEhrs ?? [];

  const locationDetailsFormDefaultValues: Partial<LocationDetailsForm> = {
    phoneNumber: location?.phoneNumber ?? '',
    address: location?.address ?? '',
    externalEhrId: location?.externalEhrId ?? '',
    externalId: location?.externalId ?? '',
    businessName: location?.businessName ?? '',
    placeOfServiceCode: location?.placeOfServiceCode ?? '',
    taxId: location?.taxId ?? '',
    replyToEmail: location?.replyToEmail ?? '',
  };

  const locationDetailsForm = useForm<LocationDetailsForm>({
    resolver: zodResolver(locationDetailsFormSchema),
    defaultValues: locationDetailsFormDefaultValues,
    values: {
      externalEhrId: location?.externalEhrId ?? '',
      address: location?.address ?? '',
      externalId: location?.externalId ?? '',
      replyToEmail: location?.replyToEmail ?? '',
    },
  });

  const effectiveLogoUrl = location?.logoUrl ?? '';

  const [updateLocationDetails] = useMutation(updateLocationDetailsMutation, {
    onCompleted: () => {
      toast.success(t('tenantAdmin.location.detailsUpdated'));
    },
    onError: () => {
      toast.error(t('tenantAdmin.location.detailsUpdateFailed'));
    },
  });

  const [updateLocationLogo, { loading: isUploadingLogo }] = useMutation(
    updateLocationLogoMutation,
    {
      onCompleted: () => toast.success(t('tenantAdmin.location.logoUpdated')),
      onError: () => toast.error(t('tenantAdmin.location.logoUpdateFailed')),
    },
  );

  const handleLogoSelect = async (file: File) => {
    const fileExt = file.name.split('.').pop();
    if (!fileExt) {
      toast.error(t('tenantAdmin.common.invalidFileExtension'));
      return;
    }
    try {
      const result = await uploadGenericFileToGCS({
        file,
        mimeType: 'image/*',
        fileExt,
      });
      if (!result.url) {
        toast.error(t('tenantAdmin.location.logoUploadFailed'));
        return;
      }
      await updateLocationLogo({
        variables: { locationId, tenantId, logoUrl: result.url },
      });
    } catch {
      toast.error(t('tenantAdmin.location.logoUploadFailed'));
    }
  };

  const handleLogoRemove = () =>
    updateLocationLogo({ variables: { locationId, tenantId, logoUrl: '' } });

  const [updateDisplayName, { loading: isRenaming }] = useMutation(
    updateLocationDisplayNameMutation,
    {
      onCompleted: () =>
        toast.success(t('tenantAdmin.location.displayNameUpdated')),
      onError: () =>
        toast.error(t('tenantAdmin.location.displayNameUpdateFailed')),
    },
  );

  const [createNewEhrFormOpen, setCreateNewEhrFormOpen] =
    useState<boolean>(false);
  const [isAddUserOpen, setIsAddUserOpen] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const onSubmitLocationDetails = async (data: LocationDetailsForm) => {
    try {
      await updateLocationDetails({
        variables: {
          locationId,
          tenantId,
          address: data.address ?? '',
          phoneNumber: data.phoneNumber ?? '',
          // We don't want to pass an empty string here since its for a foreign key
          externalEhrId: data.externalEhrId || null,
          // Empty string must become null too, otherwise an empty external_id
          // makes the location read as EMR-synced (see isSyncedLocation).
          externalId: data.externalId || null,
          businessName: data.businessName ?? null,
          placeOfServiceCode: data.placeOfServiceCode ?? null,
          taxId: data.taxId ?? null,
          replyToEmail: data.replyToEmail ?? null,
        },
      });

      locationDetailsForm.reset(undefined, { keepValues: true });
    } catch {
      toast.error(t('tenantAdmin.location.detailsUpdateFailed'));
    }
  };

  if (!location?.id) {
    return <Navigate to="/not-found" />;
  }

  const isSyncedLocation = Boolean(location.externalId);
  const isRenamed =
    location.sourceName != null && location.name !== location.sourceName;

  // The ltree path is a chain of location ids (root...self); resolve ancestor
  // names from the tenant's locations, skipping the root (it IS the org).
  const locationsById = new Map(
    (data.tenantsByPk?.locations ?? []).map((l) => [l.id, l]),
  );
  const ancestors = (location.path ?? '')
    .split('.')
    .filter((id) => id && id !== location.id)
    .flatMap((id) => {
      const ancestor = locationsById.get(id);
      return ancestor && !ancestor.isRoot ? [ancestor] : [];
    });

  return (
    <div>
      <div className="grid gap-5">
        <div className="my-2 py-2">
          <Stack gap={2} align="start">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/organizations/${tenantId}/admin/locations`}>
                      {t('tenantAdmin.locations.title')}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {ancestors.map((ancestor) => (
                  <React.Fragment key={ancestor.id}>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link
                          to={`/organizations/${tenantId}/admin/locations/${ancestor.id}`}
                        >
                          {ancestor.name}
                        </Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </React.Fragment>
                ))}
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{location.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <Stack direction="horizontal" gap={4} align="center">
              <div className="group relative size-20 shrink-0">
                <SimpleTooltip
                  label={
                    effectiveLogoUrl
                      ? t('tenantAdmin.location.changeLogoTooltip')
                      : t('tenantAdmin.location.uploadLogoTooltip')
                  }
                >
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    className="flex size-20 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-border-primary bg-bg-secondary"
                  >
                    {effectiveLogoUrl ? (
                      <img
                        alt={t('tenantAdmin.location.logoAlt', {
                          name: location.name,
                        })}
                        className="max-h-full max-w-full"
                        src={effectiveLogoUrl}
                      />
                    ) : (
                      <Icon
                        name="MapPinned"
                        size="lg"
                        className="text-text-secondary"
                      />
                    )}
                    <div className="absolute inset-0 hidden items-center justify-center rounded-xl bg-black/50 group-hover:flex">
                      <Icon name="Upload" size="sm" className="text-white" />
                    </div>
                    <span className="sr-only">
                      {t('tenantAdmin.location.uploadLogo')}
                    </span>
                  </button>
                </SimpleTooltip>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  disabled={isUploadingLogo}
                  onChange={(e) => {
                    const file = e.currentTarget.files?.[0];
                    if (file) {
                      handleLogoSelect(file);
                    }
                    e.currentTarget.value = '';
                  }}
                />
                {effectiveLogoUrl && (
                  <SimpleTooltip label={t('tenantAdmin.location.removeLogo')}>
                    <button
                      type="button"
                      disabled={isUploadingLogo}
                      className="absolute -top-1.5 -right-1.5 hidden size-5 items-center justify-center rounded-full border border-border-primary bg-bg-primary text-text-secondary shadow-sm group-hover:flex hover:text-bg-negative-strong-primary"
                      onClick={handleLogoRemove}
                    >
                      <Icon name="X" size="xs" />
                      <span className="sr-only">
                        {t('tenantAdmin.location.removeLogo')}
                      </span>
                    </button>
                  </SimpleTooltip>
                )}
              </div>
              <Stack gap={1} align="start" fullWidth={false}>
                <EditableLocationName
                  name={location.name}
                  isSaving={isRenaming}
                  onSave={(next) =>
                    updateDisplayName({
                      variables: { tenantId, locationId, name: next },
                    })
                  }
                />
                {isSyncedLocation && (
                  <Stack
                    direction="horizontal"
                    gap={2}
                    align="center"
                    fullWidth={false}
                  >
                    <Badge variant="infoSubtle" uppercase>
                      {t('tenantAdmin.location.autoCreatedBadge')}
                    </Badge>
                    {isRenamed && (
                      <Badge variant="warningSubtle" uppercase>
                        {t('tenantAdmin.location.renamedBadge')}
                      </Badge>
                    )}
                    <Text size="sm" color="secondary" className="font-mono">
                      {location.externalId}
                    </Text>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </div>
      </div>

      <NewEhrForm
        onOpenChange={setCreateNewEhrFormOpen}
        open={createNewEhrFormOpen}
      />
      <div className="grid gap-5">
        <Card className="w-full rounded-xl border-border-primary">
          <EluveCardHeader>
            <Text>{t('tenantAdmin.location.detailsTitle')}</Text>
            <Button
              type="button"
              variant={showAdvanced ? 'secondary' : 'outline'}
              size="xs"
              onClick={() => setShowAdvanced((prev) => !prev)}
            >
              <Icon name="Settings2" size="auto" />
              {showAdvanced
                ? t('tenantAdmin.location.hideAdvancedSettings')
                : t('tenantAdmin.location.showAdvancedSettings')}
            </Button>
          </EluveCardHeader>
          <EluveCardContent>
            <Form {...locationDetailsForm}>
              <form
                onSubmit={locationDetailsForm.handleSubmit(
                  onSubmitLocationDetails,
                )}
              >
                <Stack gap={6} align="stretch">
                  {showAdvanced && (
                    <>
                      <Stack className="max-w-md" gap={4} align="start">
                        <FormField
                          control={locationDetailsForm.control}
                          name="externalEhrId"
                          render={({ field, fieldState: { error } }) => (
                            <FormItem>
                              <FormLabel>
                                {t('tenantAdmin.location.externalEhrLabel')}
                              </FormLabel>
                              <Stack
                                direction="horizontal"
                                gap={2}
                                align="center"
                              >
                                {Boolean(tenantExternalEhrs.length) && (
                                  <>
                                    <Combobox>
                                      <ComboboxSelectButton className="w-96 rounded-lg bg-white">
                                        <Stack
                                          className="truncate text-sm"
                                          direction="horizontal"
                                          fullWidth={false}
                                          gap={2}
                                        >
                                          {(() => {
                                            const selectEhr =
                                              tenantExternalEhrs.find(
                                                (ehr) => ehr.id === field.value,
                                              );
                                            return selectEhr
                                              ? `${selectEhr.vendor} - ${selectEhr.domain}`
                                              : t(
                                                  'tenantAdmin.location.selectEhr',
                                                );
                                          })()}
                                        </Stack>
                                      </ComboboxSelectButton>
                                      <ComboboxDropdown
                                        searchPlaceholder={t(
                                          'tenantAdmin.location.selectEhr',
                                        )}
                                      >
                                        {tenantExternalEhrs.map((ehr) => (
                                          <ComboboxOption
                                            key={ehr.id}
                                            onSelect={() =>
                                              field.onChange(ehr.id)
                                            }
                                          >
                                            <ComboboxSelectCheck
                                              className="mr-2"
                                              selected={field.value === ehr.id}
                                            />
                                            {`${ehr.vendor} - ${ehr.domain}`}
                                          </ComboboxOption>
                                        ))}
                                      </ComboboxDropdown>
                                    </Combobox>
                                    {Boolean(field.value) && (
                                      <SimpleTooltip
                                        label={t(
                                          'tenantAdmin.location.removeEhrTooltip',
                                        )}
                                      >
                                        <Button
                                          variant="ghost"
                                          type="button"
                                          onClick={() => {
                                            field.onChange('');
                                          }}
                                        >
                                          <Icon name="X" size="auto" />
                                        </Button>
                                      </SimpleTooltip>
                                    )}
                                    <Text
                                      className="mx-2 italic"
                                      color="secondary"
                                    >
                                      {t('tenantAdmin.location.or')}
                                    </Text>
                                  </>
                                )}
                                <Button
                                  type="button"
                                  variant="default"
                                  onClick={() => setCreateNewEhrFormOpen(true)}
                                >
                                  {t('tenantAdmin.location.createNewEhr')}
                                </Button>
                              </Stack>
                              {error && (
                                <FormMessage className="mt-4">
                                  {error.message}
                                </FormMessage>
                              )}
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={locationDetailsForm.control}
                          name="externalId"
                          render={({ field, fieldState: { error } }) => (
                            <FormItem>
                              <FormLabel>
                                {t('tenantAdmin.location.externalIdLabel')}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={t(
                                    'tenantAdmin.location.externalIdLabel',
                                  )}
                                  {...field}
                                />
                              </FormControl>
                              {error && (
                                <FormMessage className="mt-4">
                                  {error.message}
                                </FormMessage>
                              )}
                            </FormItem>
                          )}
                        />
                      </Stack>
                      <Separator />
                    </>
                  )}
                  <Stack gap={6} align="stretch">
                    {isSyncedLocation && (
                      <>
                        <SyncedSourceGrid location={location} />
                        <Separator />
                      </>
                    )}

                    <div className="grid max-w-3xl grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                      <FormField
                        control={locationDetailsForm.control}
                        name="address"
                        render={({ field, fieldState: { error } }) => (
                          <FormItem>
                            <FormLabel>
                              {t('tenantAdmin.location.addressLabel')}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t(
                                  'tenantAdmin.location.addressLabel',
                                )}
                                {...field}
                              />
                            </FormControl>
                            {error && (
                              <FormMessage className="mt-4">
                                {error.message}
                              </FormMessage>
                            )}
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={locationDetailsForm.control}
                        name="phoneNumber"
                        render={({ field, fieldState: { error } }) => (
                          <FormItem>
                            <FormLabel>
                              {t('tenantAdmin.location.phoneNumberLabel')}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t(
                                  'tenantAdmin.location.phoneNumberLabel',
                                )}
                                {...field}
                              />
                            </FormControl>
                            {error && (
                              <FormMessage className="mt-4">
                                {error.message}
                              </FormMessage>
                            )}
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator />

                    <Stack gap={3} align="stretch">
                      <Stack gap={0} align="start">
                        <Text size="sm" weight="medium">
                          {t('tenantAdmin.location.inheritedDefaultsTitle')}
                        </Text>
                        <Text size="xs" color="secondary">
                          {t(
                            'tenantAdmin.location.inheritedDefaultsDescription',
                          )}
                        </Text>
                      </Stack>
                      <div className="grid max-w-3xl grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                        <FormField
                          control={locationDetailsForm.control}
                          name="businessName"
                          render={({ field, fieldState: { error } }) => (
                            <FormItem>
                              <FormLabel>
                                {t('tenantAdmin.location.businessNameLabel')}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={t(
                                    'tenantAdmin.location.businessNameLabel',
                                  )}
                                  {...field}
                                />
                              </FormControl>
                              {error && (
                                <FormMessage className="mt-4">
                                  {error.message}
                                </FormMessage>
                              )}
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={locationDetailsForm.control}
                          name="placeOfServiceCode"
                          render={({ field, fieldState: { error } }) => (
                            <FormItem>
                              <FormLabel>
                                {t('tenantAdmin.location.posCodeLabel')}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={t(
                                    'tenantAdmin.location.posCodeLabel',
                                  )}
                                  {...field}
                                />
                              </FormControl>
                              {error && (
                                <FormMessage className="mt-4">
                                  {error.message}
                                </FormMessage>
                              )}
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={locationDetailsForm.control}
                          name="taxId"
                          render={({ field, fieldState: { error } }) => (
                            <FormItem>
                              <FormLabel>
                                {t('tenantAdmin.location.taxIdLabel')}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={t(
                                    'tenantAdmin.location.taxIdLabel',
                                  )}
                                  {...field}
                                />
                              </FormControl>
                              {error && (
                                <FormMessage className="mt-4">
                                  {error.message}
                                </FormMessage>
                              )}
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={locationDetailsForm.control}
                          name="replyToEmail"
                          render={({ field, fieldState: { error } }) => (
                            <FormItem>
                              <FormLabel>
                                {t('tenantAdmin.location.replyToEmailLabel')}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={t(
                                    'tenantAdmin.location.replyToEmailLabel',
                                  )}
                                  {...field}
                                  value={field.value}
                                />
                              </FormControl>
                              {error && (
                                <FormMessage className="mt-4">
                                  {error.message}
                                </FormMessage>
                              )}
                            </FormItem>
                          )}
                        />
                      </div>
                    </Stack>
                  </Stack>
                </Stack>
                <div className="mt-6 flex justify-end border-t border-border-primary pt-4">
                  <Button
                    type="submit"
                    disabled={!locationDetailsForm.formState.isDirty}
                  >
                    {t('tenantAdmin.common.save')}
                  </Button>
                </div>
              </form>
            </Form>
          </EluveCardContent>
        </Card>
        <SyncedLocationStats tenantId={tenantId} locationId={locationId} />
        <Card className="w-full">
          <EluveCardHeader>
            <Stack direction="horizontal" justify="between" align="center">
              <Text>{t('tenantAdmin.common.users')}</Text>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => setIsAddUserOpen(true)}
              >
                <Icon name="Plus" size="auto" />
                {t('tenantAdmin.location.addUser')}
              </Button>
            </Stack>
          </EluveCardHeader>
          <EluveCardContent className="p-0">
            <LocationUsers
              locationId={locationId}
              tenantId={tenantId}
              isAddUserOpen={isAddUserOpen}
              onAddUserOpenChange={setIsAddUserOpen}
            />
          </EluveCardContent>
        </Card>
        <FeatureFlaggedComponent flag="BILLING_CODE_PRICES">
          <Card className="w-full">
            <EluveCardHeader>
              <Text>{t('tenantAdmin.location.billingCodesTitle')}</Text>
            </EluveCardHeader>
            <EluveCardContent>
              <LocationBillingCodes
                locationId={locationId}
                tenantId={tenantId}
              />
            </EluveCardContent>
          </Card>
        </FeatureFlaggedComponent>
      </div>
    </div>
  );
};

export const LocationPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <SettingsPageFrame
      size="wide"
      parentTitle={t('tenantAdmin.locations.title')}
      backTo=".."
    >
      <LocationContent />
    </SettingsPageFrame>
  );
};
